/*!
 * index.js — «Живые открытки»: состояние, панели, связь со сценой и выгрузкой.
 *
 * Путь человека, под который здесь всё подчинено: повод → готовая открытка
 * уже живёт на экране → «Кому» → «Отправить». Остальное (стиль, анимация,
 * украшения, фото, музыка) — в панелях и не мешает этому пути.
 *
 * Суточный лимит (window.__toolLimit) — только у анимированных файлов (видео
 * и GIF), единица — ОТКРЫТКА (ключ cardKey): пересохранить ту же открытку в
 * другом формате бесплатно. Картинка и ссылка — без счётчика. Логика
 * списания — в exporter.js; здесь страница только знает свой ключ.
 *
 * Работа переживает крах, F5 и закрытую вкладку — общий контроллер «Мои
 * работы» (window.__toolDraft.editor, public/js/tool-draft-editor.js): тексты
 * и дизайн — состоянием, фото, своя песня и голос — байтами.
 */

import { TEMPLATES, templatesFor, templateById, buildDoc } from './templates.js';
import { normalizeDoc, FORMATS, frameSize, encodeLink, decodeLink } from './scene.js';
import { OCCASIONS, orderForStrip, defaultOccasion, nextDate, isDated } from './occasions.js';
import { Stage } from './stage.js';
import { Scene } from './render.js';
import { buildBackground } from './backgrounds.js';
import { PALETTES, PARTICLE_KINDS, makeCanvas } from './particles.js';
import { FILLS } from './textfx.js';
import { STICKER_ANIMS, ensureStickers } from './decor.js';
import * as Fonts from './fonts.js';
import * as Text from './textgen.js';
import { Exporter } from './exporter.js';
import { Music } from './music.js';
import { openViewer } from './viewer.js';

const C = window.__GC || {};
const U = C.ui || {};
const SLUG = 'greeting-card-maker';
const $ = (id) => document.getElementById(id);
/* Подстановка — одним проходом по целым именам: последовательная замена
   «:n» съедала начало «:name» («Открытка 1 из 3 — 3ame»). */
const u = (k, r) => {
    const s = U[k] != null ? String(U[k]) : k;
    if (!r) return s;
    return s.replace(/:([a-z_]+)/g, (m, name) => (Object.prototype.hasOwnProperty.call(r, name) ? String(r[name]) : m));
};
const settings = window.__toolSettings || { save() {}, load(_s, _k, f) { return f; } };
const funnel = (stage, data) => { try { window.__aeFunnel && window.__aeFunnel(stage, data || {}); } catch (e) { /* телеметрия не роняет тул */ } };

/* ── состояние ────────────────────────────────────────────────────────── */

const S = {
    locale: C.locale || 'ru',
    rtl: !!C.rtl,
    occ: null,
    tplId: null,
    headIdx: 0,
    wishIdx: 0,
    head: '',
    headUser: false,
    body: '',
    bodyUser: false,
    bodySeed: null,     // вариант словаря, из которого собран текст (для «много имён»)
    name: '',
    salute: settings.load(SLUG, 'salute', ''),
    // Род адресата там, где его не вывести из имени (Text.GENDERED): от него
    // зависят обращение («Querida/Querido») и согласование в тексте.
    gender: settings.load(SLUG, 'gender', 'female') === 'male' ? 'male' : 'female',
    sign: settings.load(SLUG, 'sign', ''),
    tone: 'warm', form: 'ty', len: 'short',
    show: { head: true, name: true, body: true, sign: true },
    doc: null,
    photo: null,        // ImageBitmap для сцены
    photoFile: null,    // байты фото — для черновика (File, как выбран)
    photoId: '',        // имя фото для единицы лимита
    prevBg: null,
    selection: null,
};

/**
 * Единица суточного лимита — открытка КОМУ-ТО с ЭТИМИ словами: имя,
 * заголовок, поздравление, подпись, фото и голос. Та же открытка в другом
 * формате, размере, с другой анимацией или мелодией — та же единица
 * (сохранить ещё раз можно); другое имя — другая открытка. Отпечаток, а не
 * случайный ключ на сессию: иначе одну открытку переписывали бы под десять
 * имён за одну единицу, и «одна открытка — много имён» ничего бы не стоило.
 */
function docKey(d) {
    const txt = (r) => { const t = d && d.texts.find((x) => x.role === r); return t && t.on !== false ? t.text : ''; };
    const parts = [
        txt('name'), txt('head'), txt('body'), txt('sign'),
        S.photo ? S.photoId : '',
        music ? music.voiceId() : '',
    ].map((x) => String(x || '').trim().replace(/\s+/g, ' ').toLowerCase());
    return 'gc-' + fnv(parts.join('␟'));
}

function cardKey() { return docKey(S.doc); }

function fnv(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    return (h >>> 0).toString(36);
}

const rid = () => Math.random().toString(36).slice(2, 10);

/** Поводы, у которых есть перевод на этой локали, — только они в ленте. */
const OCC_KEYS = Object.keys(C.occ || {}).filter((k) => OCCASIONS[k]);

/* ── что открытка говорит на этом языке ─────────────────────────────────
   Каталог шаблонов общий, а праздники и приличия — нет:
   · «Другие шаблоны» показывают только дизайны поводов этого языка —
     георгиевская лента (День Победы) не появляется на украинской странице,
     где этот символ запрещён, и на немецкой, где он политический;
   · на арабской и индонезийской страницах нет наклеек с алкоголем —
     бокалы и шампанское в шаблонах заменяются на бенгальский огонь и салют;
   · пятничная открытка по-арабски — «Джума мубарак», и открывается она
     мечетью, а не кофе и коктейлем у моря. */
const NO_ALCOHOL = new Set(['ar', 'id']);
const ALCOHOL_SWAP = { champagne: 'sparkler', glasses: 'fireworks', wine: 'gift', cocktail: 'sun' };
const OCC_EMOJI = { ar: { weekend: '🕌' } };
const TPL_PREFER = {
    ar: { weekend: ['eid-lanterns'], newyear: ['fireworks-gold', 'neon-party'] },
    id: { newyear: ['fireworks-gold', 'neon-party'] },
};

function occEmoji(k) {
    return (OCC_EMOJI[S.locale] && OCC_EMOJI[S.locale][k]) || OCCASIONS[k].emoji;
}

/** Шаблоны повода на этом языке: свои предпочтения языка — первыми. */
function templatesHere(occ) {
    const own = templatesFor(occ);
    const pref = ((TPL_PREFER[S.locale] || {})[occ] || []).map(templateById).filter(Boolean);
    return pref.concat(own.filter((t) => !pref.includes(t)));
}

/** Дизайн повода, которого на этом языке нет (День Победы на uk), не
 *  предлагается и среди «других». */
function templateSpeaksHere(t) {
    return t.occ.some((o) => OCC_KEYS.includes(o));
}

/** Документ шаблона, приведённый к приличиям языка. */
function localizeDoc(d) {
    if (NO_ALCOHOL.has(S.locale)) {
        for (const x of d.deco) if (x.kind === 'sticker' && ALCOHOL_SWAP[x.id]) x.id = ALCOHOL_SWAP[x.id];
    }
    return d;
}
const EVERGREEN = ['birthday', 'morning', 'night', 'day', 'evening', 'love', 'thanks', 'congrats', 'jubilee', 'wedding', 'anniversary', 'baby', 'justbecause', 'weekend', 'housewarming', 'graduation', 'retirement', 'recovery', 'sympathy'];

let stage = null;
let music = null;
let exporter = null;

/* ── документ ─────────────────────────────────────────────────────────── */

/** Род адресата: по-русски — из имени (движок ФИО); на языках с родом —
 *  переключатель «для неё / для него»; где рода нет — не важен. */
function genderOf(name) {
    if (S.locale === 'ru') return Text.saluteGender(S.salute) || Text.nameInfo(name, S.locale).gender;
    return Text.GENDERED.has(S.locale) ? S.gender : null;
}

function nameText() {
    return Text.nameLine(S.name, S.salute, S.locale, genderOf(S.name), S.occ);
}

/* Обращения — сами слова, которые встанут на открытку. У русского род
   берёт движок ФИО из имени; у остальных языков вариант на каждый род
   (разметка блейда — русская, её список здесь заменяется). */
function renderSaluteOptions() {
    if (S.locale === 'ru') return;
    const sel = $('gcSalute');
    const keep = S.salute;
    sel.textContent = '';
    const none = document.createElement('option');
    none.value = '';
    none.textContent = u('salute_none');
    sel.appendChild(none);
    for (const o of Text.saluteOptions(S.locale, S.gender)) {
        const op = document.createElement('option');
        op.value = o.v;
        op.textContent = o.word;
        sel.appendChild(op);
    }
    S.salute = [...sel.options].some((o) => o.value === keep) ? keep : '';
}

/** Собрать документ заново из шаблона и того, что человек уже написал. */
function rebuildFromTemplate(id, opts) {
    // Выбор — адрес в старом документе (индекс наклейки): в новом он чужой.
    if (stage && stage.selection) stage.select(null);
    const keep = S.doc;
    const doc = buildDoc(id, {
        occ: S.occ,
        head: S.head,
        name: nameText(),
        body: S.body,
        sign: S.sign,
        fmt: keep ? keep.fmt : settings.load(SLUG, 'fmt', '1:1'),
        keep: null,
    });
    localizeDoc(doc);
    // Перенос правок человека в новый дизайн: фото, музыка, формат, показ строк.
    if (keep) {
        doc.photo = keep.photo;
        if (keep.music && keep.music.src && !String(keep.music.src).startsWith('lib:')) doc.music = keep.music;
        else if (keep.music && keep.music.user) doc.music = keep.music;
        if (keep.bg && keep.bg.kind === 'photo') doc.bg = keep.bg;
        // Наклейки, которые человек добавил сам, переезжают в новый шаблон.
        const own = (keep.deco || []).filter((d) => d.user);
        doc.deco = doc.deco.concat(own);
    }
    for (const t of doc.texts) t.on = S.show[t.role] !== false;
    S.tplId = id;
    S.doc = doc;
    applyDoc(opts && opts.replay !== false);
}

let applyTimer = 0;
function applyDoc(replay) {
    syncTexts();
    stage.setDoc(S.doc, { replay: !!replay });
    music.setDoc(S.doc);
    touch(true);
    reflectPanels();
}

/** Тексты из полей в документ (без пересборки дизайна). */
function syncTexts() {
    const d = S.doc;
    if (!d) return;
    const set = (role, text) => { const t = d.texts.find((x) => x.role === role); if (t) { t.text = text; t.on = S.show[role] !== false; } };
    set('head', Text.fillYear(S.head));
    set('name', nameText());
    set('body', S.body);
    set('sign', S.sign);
}

function textsChanged() {
    clearTimeout(applyTimer);
    applyTimer = setTimeout(() => {
        syncTexts();
        stage.setDoc(S.doc);
        touch(true);
        refreshThumbTexts();
    }, 120);
}

/** live — ползунок в движении: сохраняет начатую работу, но не начинает её
 *  (законченный шаг ползунка засчитывает общий обработчик change). */
function docChanged(replay, live) {
    stage.setDoc(S.doc, { replay: !!replay });
    music.setDoc(S.doc);
    if (S.selection) stage.drawSelection();
    touch(!live);
}

/* ── повод ────────────────────────────────────────────────────────────── */

function setOccasion(occ, why) {
    if (!OCC_KEYS.includes(occ)) occ = OCC_KEYS[0];
    S.occ = occ;
    S.headIdx = 0;
    S.wishIdx = 0;
    if (!S.headUser) S.head = Text.headline(occ, 0);
    if (!S.bodyUser) { S.body = Text.shortWish(occ, 0); S.bodySeed = null; }
    const list = templatesHere(occ);
    const keepTpl = S.tplId && list.some((t) => t.id === S.tplId) && why !== 'init';
    rebuildFromTemplate(keepTpl ? S.tplId : seasonalFirst(list).id);
    renderOccStrip();
    renderTemplates();
    fillTextFields();
    funnel('occasion', { occ, why: why || 'click' });
}

/** Сезонный шаблон — первым: осенью «Доброе утро» открывается осенним. У
 *  праздника с датой первым остаётся его собственный шаблон: в День учителя
 *  — книги и яблоко, а не «осеннее утро». */
function seasonalFirst(list, occ) {
    if (isDated(occ || S.occ, S.locale)) return list[0];
    const m = new Date().getMonth() + 1;
    const season = m >= 9 && m <= 11 ? 'autumn' : m === 12 || m <= 2 ? 'winter' : m <= 5 ? 'spring' : 'summer';
    const tagged = list.find((t) => (t.season || tplSeason(t.id)) === season);
    return tagged || list[0];
}
function tplSeason(id) {
    return { 'autumn-morning': 'autumn', 'pokrov-autumn': 'autumn', 'halloween-night': 'autumn', 'winter-magic': 'winter', 'christmas-lights': 'winter', 'spring-tulips': 'spring', 'easter-light': 'spring', 'sea-summer': 'summer' }[id] || null;
}

/* Адрес страницы повода не хранит: ?occ= / ?tpl= — это вход СНАРУЖИ
   (ссылка из рубрики, поиска, соцсети), он читается один раз и убирается.
   Иначе F5 с «?occ=» в адресе выглядел бы как новый вход и не вернул бы
   открытку этой вкладки. */
function urlWith(params) {
    const url = new URL(location.href);
    for (const [k, v] of Object.entries(params)) { if (v) url.searchParams.set(k, v); else url.searchParams.delete(k); }
    return url.pathname + url.search + url.hash;
}

function dayBadge(days) {
    if (days == null) return '';
    if (days === 0) return u('occ_today');
    if (days === 1) return u('occ_tomorrow');
    return u('occ_in_days', { n: days });
}

function occChip(k, days) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gc-oc' + (k === S.occ ? ' on' : '') + (days === 0 ? ' today' : '');
    b.setAttribute('role', 'tab');
    b.setAttribute('aria-selected', k === S.occ ? 'true' : 'false');
    b.dataset.occ = k;
    const e = document.createElement('span'); e.className = 'e'; e.textContent = occEmoji(k); e.setAttribute('aria-hidden', 'true');
    b.appendChild(e);
    b.appendChild(document.createTextNode(Text.occStrings(k).label));
    const badge = dayBadge(days);
    if (badge) { const d = document.createElement('span'); d.className = 'd'; d.textContent = badge; b.appendChild(d); }
    b.addEventListener('click', () => { closeOccMenu(); setOccasion(k, 'click'); });
    return b;
}

function renderOccStrip() {
    const strip = $('gcOccStrip');
    const now = new Date();
    const soon = orderForStrip(OCC_KEYS.filter((k) => isDated(k, S.locale)), S.locale, now).filter((x) => x.days != null);
    const seen = new Set();
    const items = [];
    for (const s of soon) { items.push(s); seen.add(s.key); }
    for (const k of EVERGREEN) if (OCC_KEYS.includes(k) && !seen.has(k)) { items.push({ key: k, days: null }); seen.add(k); }
    // Выбранный повод, если его нет в ленте (открыт из «Все поводы»), — вторым.
    if (S.occ && !seen.has(S.occ)) items.splice(Math.min(1, items.length), 0, { key: S.occ, days: (nextDate(S.occ, S.locale, now) || {}).days });
    strip.textContent = '';
    for (const it of items.slice(0, 16)) strip.appendChild(occChip(it.key, it.days));
    const on = strip.querySelector('.gc-oc.on');
    if (on && on.scrollIntoView) on.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

/* «Все поводы» — поповер со всеми поводами по группам. */
const occMenu = () => $('gcOccMenu');
const popoverOk = typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;
function openOccMenu() {
    const m = occMenu();
    m.textContent = '';
    const groups = C.ui && C.ui.occ_groups ? C.ui.occ_groups : { holiday: 'Holidays', daily: 'Daily', life: 'Life', feelings: 'Feelings' };
    const now = new Date();
    for (const [g, title] of Object.entries(groups)) {
        const keys = OCC_KEYS.filter((k) => OCCASIONS[k].group === g);
        if (!keys.length) continue;
        if (g === 'holiday') keys.sort((a, b) => ((nextDate(a, S.locale, now) || { days: 999 }).days) - ((nextDate(b, S.locale, now) || { days: 999 }).days));
        const h = document.createElement('h4'); h.textContent = title; m.appendChild(h);
        const grid = document.createElement('div'); grid.className = 'gc-menu-grid';
        for (const k of keys) {
            const nd = g === 'holiday' ? nextDate(k, S.locale, now) : null;
            grid.appendChild(occChip(k, nd && nd.days <= 30 ? nd.days : null));
        }
        m.appendChild(grid);
    }
    const btn = $('gcOccAllBtn');
    m.dataset.popOpen = 'true';
    if (popoverOk) { try { m.showPopover(); } catch (e) { /* открыто атрибутом */ } }
    const r = btn.getBoundingClientRect();
    const w = Math.min(640, window.innerWidth - 32);
    m.style.left = Math.max(16, Math.min(window.innerWidth - w - 16, r.right - w)) + 'px';
    m.style.top = Math.min(window.innerHeight - 120, r.bottom + 8) + 'px';
    btn.setAttribute('aria-expanded', 'true');
}
function closeOccMenu() {
    const m = occMenu();
    if (popoverOk) { try { m.hidePopover(); } catch (e) { /* уже закрыт */ } }
    m.dataset.popOpen = 'false';
    $('gcOccAllBtn').setAttribute('aria-expanded', 'false');
}
const occMenuOpen = () => occMenu().dataset.popOpen === 'true';

/* ── шаблоны: миниатюры ───────────────────────────────────────────────── */

const thumbScene = new Scene({ locale: S.locale, rtl: S.rtl });
const thumbQueue = [];
let thumbBusy = false;
const THUMB = 176;

function thumbDoc(id) {
    const d = buildDoc(id, { occ: S.occ, head: Text.fillYear(S.head), name: nameText(), body: '', sign: '', fmt: '1:1' });
    d.texts.forEach((t) => { if (t.role === 'body' || t.role === 'sign') t.on = false; });
    d.intro = false;
    return localizeDoc(d);
}

function renderTemplates() {
    const own = templatesHere(S.occ);
    const rest = TEMPLATES.filter((t) => !own.includes(t) && templateSpeaksHere(t));
    fillTplGrid($('gcTpls'), own);
    fillTplGrid($('gcTplsMore'), rest);
}

function fillTplGrid(root, list) {
    root.textContent = '';
    for (const tpl of list) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'gc-tpl' + (tpl.id === S.tplId ? ' on' : '');
        b.dataset.tpl = tpl.id;
        b.setAttribute('aria-pressed', tpl.id === S.tplId ? 'true' : 'false');
        const cv = document.createElement('canvas');
        cv.width = THUMB; cv.height = THUMB;
        cv.setAttribute('aria-hidden', 'true');
        const cap = document.createElement('span');
        cap.textContent = (C.tpl && C.tpl[tpl.id]) || tpl.id;
        b.appendChild(cv); b.appendChild(cap);
        b.addEventListener('click', () => {
            rebuildFromTemplate(tpl.id);
            document.querySelectorAll('.gc-tpl').forEach((x) => { const on = x.dataset.tpl === tpl.id; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
            funnel('tpl_apply', { tpl: tpl.id, occ: S.occ });
        });
        root.appendChild(b);
        thumbObserver.observe(cv);
        cv.__tpl = tpl.id;
    }
}

const thumbObserver = new IntersectionObserver((es) => {
    for (const e of es) {
        if (!e.isIntersecting) continue;
        thumbObserver.unobserve(e.target);
        queueThumb(e.target);
    }
}, { rootMargin: '120px' });

function queueThumb(cv) { thumbQueue.push(cv); pumpThumbs(); }

async function pumpThumbs() {
    if (thumbBusy) return;
    thumbBusy = true;
    while (thumbQueue.length) {
        const cv = thumbQueue.shift();
        if (!cv.isConnected) continue;
        try {
            const d = thumbDoc(cv.__tpl);
            thumbScene.setSize(THUMB, THUMB, 1);
            thumbScene.setDoc(d);
            await thumbScene.ready();
            thumbScene.dirty = true;
            thumbScene.draw(cv.getContext('2d'), 2.4, { intro: false });
        } catch (e) { /* миниатюра не должна ронять страницу */ }
        await new Promise((r) => (window.requestIdleCallback ? requestIdleCallback(r, { timeout: 120 }) : setTimeout(r, 16)));
    }
    thumbBusy = false;
}

let thumbTextTimer = 0;
function refreshThumbTexts() {
    clearTimeout(thumbTextTimer);
    thumbTextTimer = setTimeout(() => {
        document.querySelectorAll('.gc-tpl canvas').forEach((cv) => {
            const r = cv.getBoundingClientRect();
            if (r.bottom > 0 && r.top < innerHeight && r.width > 0) queueThumb(cv);
            else thumbObserver.observe(cv);
        });
    }, 600);
}

/* ── вкладки ──────────────────────────────────────────────────────────── */

function showTab(tab) {
    document.querySelectorAll('.gc-rail [data-tab]').forEach((b) => {
        const on = b.dataset.tab === tab;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    document.querySelectorAll('.gc-pane').forEach((p) => { p.hidden = p.dataset.panel !== tab; });
    if (tab === 'style') warmFonts();
    if (tab === 'anim') renderAnim();
    if (tab === 'photo') renderPhotoOpts();
    if (tab === 'deco') renderStickers();
    if (tab === 'music') music.renderList();
    if (tab === 'tpl') refreshThumbTexts();
    funnel('tab', { tab });
}

/* ── текст ────────────────────────────────────────────────────────────── */

function fillTextFields() {
    $('gcHead').value = S.head;
    $('gcBody').value = S.body;
    $('gcName').value = S.name;
    $('gcSign').value = S.sign;
    $('gcSalute').value = S.salute;
    document.querySelectorAll('[data-show]').forEach((c) => { c.checked = S.show[c.dataset.show] !== false; });
}

function segValue(id, v) {
    const seg = $(id);
    seg.querySelectorAll('[data-v]').forEach((b) => b.classList.toggle('on', b.dataset.v === String(v)));
}

function bindSeg(id, fn) {
    $(id).addEventListener('click', (e) => {
        const b = e.target.closest('[data-v]');
        if (!b) return;
        segValue(id, b.dataset.v);
        fn(b.dataset.v);
    });
}

async function updateNameProof() {
    const p = $('gcNameProof');
    if (!S.name || S.locale !== 'ru') { p.hidden = true; return; }
    const E = await Text.loadFio();
    if (!E) { p.hidden = true; return; }
    const info = Text.nameInfo(S.name, S.locale);
    if (!info.names || !info.names.datv) { p.hidden = true; return; }
    p.textContent = info.declined === false ? u('name_fixed', { n: S.name }) : u('name_declined', { datv: info.names.datv, accs: info.names.accs || info.names.nomn });
    p.hidden = false;
}

/* Поздравление, подобранное кнопкой, идёт за именем: вписали другое имя —
   тот же вариант текста пересобирается с его падежами и родом. Свой текст
   человека не трогаем. */
let followSeq = 0;
async function followName() {
    if (S.bodyUser || S.bodySeed == null) return;
    const seq = ++followSeq;
    const t = await Text.bodyFor({ occ: S.occ, name: S.name, gender: genderOf(S.name), forcedGender: Text.saluteGender(S.salute), tone: S.tone, form: S.form, length: S.len, seed: S.bodySeed });
    if (!t || seq !== followSeq || S.bodyUser) return;
    if (t === S.body) return;
    S.body = t;
    $('gcBody').value = t;
    textsChanged();
}

function bindText() {
    $('gcName').addEventListener('input', (e) => {
        S.name = e.target.value;
        textsChanged();
        clearTimeout(bindText.t);
        bindText.t = setTimeout(() => { updateNameProof(); followName(); }, 350);
    });
    $('gcSalute').addEventListener('change', (e) => { S.salute = e.target.value; settings.save(SLUG, 'salute', S.salute); textsChanged(); });
    $('gcHead').addEventListener('input', (e) => { S.head = e.target.value; S.headUser = true; textsChanged(); });
    $('gcHeadNext').addEventListener('click', () => {
        S.headIdx++;
        S.head = Text.headline(S.occ, S.headIdx);
        S.headUser = false;
        $('gcHead').value = S.head;
        textsChanged();
    });
    $('gcBody').addEventListener('input', (e) => { S.body = e.target.value; S.bodyUser = true; S.bodySeed = null; textsChanged(); });
    $('gcSign').addEventListener('input', (e) => { S.sign = e.target.value; settings.save(SLUG, 'sign', S.sign); textsChanged(); });
    $('gcPick').addEventListener('click', async () => {
        const btn = $('gcPick');
        btn.disabled = true;
        try {
            const r = await Text.pickBody({ occ: S.occ, name: S.name, gender: genderOf(S.name), forcedGender: Text.saluteGender(S.salute), tone: S.tone, form: S.form, length: S.len, prev: S.body });
            if (r && r.text) { S.body = r.text; S.bodyUser = false; S.bodySeed = r.seed; $('gcBody').value = r.text; textsChanged(); }
            btn.textContent = u('txt_pick_again');
            funnel('pick_text', { occ: S.occ, tone: S.tone, form: S.form, named: !!S.name });
        } finally { btn.disabled = false; }
    });
    // «Для неё / для него» — только там, где род не вывести из имени.
    if (Text.GENDERED.has(S.locale)) {
        $('gcGenderRow').hidden = false;
        segValue('gcGender', S.gender);
        bindSeg('gcGender', (v) => {
            S.gender = v === 'male' ? 'male' : 'female';
            settings.save(SLUG, 'gender', S.gender);
            renderSaluteOptions();
            $('gcSalute').value = S.salute;
            textsChanged();
            followName();
        });
    }
    bindSeg('gcTone', (v) => { S.tone = v; });
    bindSeg('gcForm', (v) => { S.form = v; });
    bindSeg('gcLen', (v) => { S.len = v; });
    document.querySelectorAll('[data-show]').forEach((c) => c.addEventListener('change', () => {
        S.show[c.dataset.show] = c.checked;
        textsChanged();
    }));
}

/* ── оформление ───────────────────────────────────────────────────────── */

const FILL_ORDER = ['gold', 'rosegold', 'silver', 'white', 'cream', 'ink', 'red', 'pink', 'sky', 'emerald', 'rainbow', 'neon', 'neonBlue'];

function headBlock() { return S.doc.texts.find((t) => t.role === 'head'); }
function nameBlock() { return S.doc.texts.find((t) => t.role === 'name'); }
function textBlock(role) { return S.doc.texts.find((t) => t.role === role); }

/* Цвет заголовка тянет за собой строку имени (если у неё не «тихий»
   цвет); цвет поздравления — подпись. */
const FILL_GROUPS = { head: ['head'], body: ['body', 'sign'] };

function renderFills(rootId, group) {
    const root = $(rootId || 'gcFills');
    const g = group || 'head';
    const lead = textBlock(FILL_GROUPS[g][0]);
    if (!root || !lead) return;
    root.textContent = '';
    const cur = lead.style;
    const names = (C.ui && C.ui.fills) || {};
    const apply = (fn) => { for (const r of FILL_GROUPS[g]) { const t = textBlock(r); if (t) fn(t); } };
    for (const k of FILL_ORDER) {
        const f = FILLS[k];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'gc-sw' + (cur === k ? ' on' : '');
        b.title = names[k] || k;
        b.setAttribute('aria-label', names[k] || k);
        b.style.background = f.solid ? f.solid : 'linear-gradient(180deg,' + f.stops.map(([p, c]) => c + ' ' + Math.round(p * 100) + '%').join(',') + ')';
        if (f.neon) b.style.boxShadow = '0 0 10px ' + f.glow;
        b.addEventListener('click', () => {
            apply((t) => { t.style = k; });
            if (g === 'head') {
                const nb = nameBlock();
                if (nb && nb.style !== 'cream' && nb.style !== 'ink' && nb.style !== 'white') nb.style = k === 'neon' || k === 'neonBlue' ? 'white' : k;
            }
            renderFills(rootId, g);
            docChanged(false);
        });
        root.appendChild(b);
    }
    // Свой цвет.
    const b = document.createElement('label');
    b.className = 'gc-sw' + (cur === 'custom' ? ' on' : '');
    b.title = names.custom || 'Custom';
    b.style.background = 'conic-gradient(red,yellow,lime,cyan,blue,magenta,red)';
    const inp = document.createElement('input');
    inp.type = 'color';
    inp.value = lead.color || (g === 'head' ? '#e0a030' : '#fff6e0');
    inp.setAttribute('aria-label', names.custom || 'Custom');
    inp.addEventListener('input', () => {
        apply((t) => { t.style = 'custom'; t.color = inp.value; });
        docChanged(false, true);
    });
    inp.addEventListener('change', () => renderFills(rootId, g));
    b.appendChild(inp);
    root.appendChild(b);
}

/* ── размер и выравнивание надписей ───────────────────────────────────────
   Размер надписи — множитель к кеглю, подобранному по месту (1 = «как
   подобрано»). Стоит у каждого поля во вкладке «Текст» (там его и ищут,
   когда надпись не влезла) и дублируется ползунками во «Стиле». */
const SIZE_STEP = 1.08;

/* Кегль, заданный шаблоном (у заголовка многих шаблонов — 1.1): он и есть
   «100 %» для человека, и к нему возвращает «Вернуть как было». */
let tplSizes = { id: null, sizes: {} };
function baseSize(role) {
    if (tplSizes.id !== S.tplId) {
        const sizes = {};
        try { for (const t of buildDoc(S.tplId, { occ: S.occ, fmt: '1:1' }).texts) sizes[t.role] = t.size || 1; } catch (e) { /* шаблона нет — 1 */ }
        tplSizes = { id: S.tplId, sizes };
    }
    return tplSizes.sizes[role] || 1;
}
const sizePct = (t) => Math.round(((t.size || 1) / baseSize(t.role)) * 100) + '%';

function setTextSize(role, v, live) {
    const t = textBlock(role);
    if (!t) return;
    t.size = Math.round(clamp(v, 0.4, 2.2) * 1000) / 1000;
    stage.scene.dirty = true;
    stage.kick();
    if (S.selection) stage.drawSelection();
    reflectSel();
    touch(!live);
}

function reflectTextSizes() {
    if (!S.doc) return;
    document.querySelectorAll('[data-size-for]').forEach((el) => {
        const t = textBlock(el.dataset.sizeFor);
        const out = el.querySelector('.gc-step-v');
        if (out && t) out.textContent = sizePct(t);
    });
    const hb = textBlock('head'), bb = textBlock('body');
    if ($('gcHeadSize') && hb) $('gcHeadSize').value = Math.round(((hb.size || 1) / baseSize('head')) * 100);
    if ($('gcBodySize') && bb) $('gcBodySize').value = Math.round(((bb.size || 1) / baseSize('body')) * 100);
    if ($('gcAlignHead') && hb) segValue('gcAlignHead', hb.align || 'center');
    if ($('gcAlignBody') && bb) segValue('gcAlignBody', bb.align || 'center');
}

function bindTextSizes() {
    document.querySelectorAll('[data-size-for]').forEach((el) => {
        const role = el.dataset.sizeFor;
        const dec = el.querySelector('[data-step="-1"]');
        const inc = el.querySelector('[data-step="1"]');
        stepButton(dec, () => { const t = textBlock(role); if (t) setTextSize(role, (t.size || 1) / SIZE_STEP); });
        stepButton(inc, () => { const t = textBlock(role); if (t) setTextSize(role, (t.size || 1) * SIZE_STEP); });
    });
}

/* ── раскладка: где картинка, где надписи ────────────────────────────── */

const HERO_MODES = ['top', 'bottom', 'side', 'behind'];

function heroSticker() { return S.doc.deco.find((d) => d.role === 'hero') || null; }
function hasHero() {
    const photoHero = !!(S.doc.photo && S.photo && S.doc.bg.kind !== 'photo');
    return photoHero || !!heroSticker();
}

function reflectLayout() {
    if (!S.doc || !$('gcHeroMode')) return;
    const any = hasHero();
    $('gcHeroRow').hidden = !any;
    $('gcHeroSizeRow').hidden = !any;
    if (any) {
        const mode = stage.scene.build().lay.heroMode;
        segValue('gcHeroMode', mode);
        const photoHero = !!(S.doc.photo && S.photo && S.doc.bg.kind !== 'photo');
        const k = photoHero ? (S.doc.photo.size || 1) : (S.doc.layout.heroScale || 1);
        $('gcHeroSize').value = Math.round(k * 100);
    }
    segValue('gcValign', S.doc.layout.valign || 'middle');
}

function bindLayout() {
    bindSeg('gcHeroMode', (v) => {
        if (!HERO_MODES.includes(v)) return;
        S.doc.layout.hero = v;
        S.doc.layout.heroFixed = true;
        // Главная наклейка возвращается на место раскладки — иначе новое
        // место картинки не было бы видно.
        const h = heroSticker();
        if (h) delete h.free;
        docChanged(false);
        reflectLayout();
        if (S.selection) stage.drawSelection();
        funnel('layout_hero', { mode: v });
    });
    $('gcHeroSize').addEventListener('input', (e) => {
        const k = Number(e.target.value) / 100;
        const photoHero = !!(S.doc.photo && S.photo && S.doc.bg.kind !== 'photo');
        if (photoHero) S.doc.photo.size = clamp(k, 0.4, 1.8);
        else {
            const L = S.doc.layout;
            const h = heroSticker();
            // Сдвинутая руками главная наклейка растёт вместе со своим местом.
            if (h && h.free) h.size = clamp((h.size || 0.3) * (k / (L.heroScale || 1)), 0.04, 1.2);
            L.heroScale = clamp(k, 0.5, 1.8);
        }
        docChanged(false, true);
        if (S.selection) stage.drawSelection();
        reflectSel();
    });
    bindSeg('gcValign', (v) => {
        S.doc.layout.valign = ['top', 'middle', 'bottom'].includes(v) ? v : 'middle';
        docChanged(false);
        funnel('layout_valign', { v });
    });
    $('gcLayReset').addEventListener('click', resetLayout);
}

/** «Вернуть всё на места»: надписи и картинка — туда и того размера, что
 *  задаёт шаблон; цвета, шрифты, наклейки и тексты остаются. */
function resetLayout() {
    const tpl = buildDoc(S.tplId, { occ: S.occ, fmt: S.doc.fmt });
    const L = S.doc.layout;
    L.hero = tpl.layout.hero;
    L.heroFixed = false;
    L.heroScale = 1;
    L.valign = tpl.layout.valign;
    for (const t of S.doc.texts) {
        const o = tpl.texts.find((x) => x.role === t.role);
        t.pos = null;
        t.size = o ? o.size : 1;
        if (o) t.align = o.align;
    }
    const h = heroSticker();
    const th = tpl.deco.find((d) => d.role === 'hero');
    if (h) { delete h.free; h.rot = th && th.rot ? th.rot : 0; h.flip = !!(th && th.flip); if (!h.flip) delete h.flip; }
    if (S.doc.photo) { S.doc.photo.pos = null; S.doc.photo.size = 1; }
    stage.select(null);
    docChanged(false);
    reflectPanels();
    funnel('layout_reset', {});
}

let fontsWarm = false;
function warmFonts() {
    renderFills('gcFills', 'head');
    renderFills('gcFillsBody', 'body');
    reflectTextSizes();
    reflectLayout();
    renderFonts('gcFonts', 'head');
    renderFonts('gcFontsBody', 'body');
    renderBgs();
    if (fontsWarm) return;
    fontsWarm = true;
    for (const f of Fonts.fontsFor(S.locale)) {
        Fonts.load(f.id, S.locale).then(() => {
            document.querySelectorAll('.gc-font[data-font="' + f.id + '"]').forEach((b) => b.classList.add('ready'));
        });
    }
}

function renderFonts(rootId, role) {
    const root = $(rootId);
    root.textContent = '';
    const blk = S.doc.texts.find((t) => t.role === role);
    const sample = role === 'head' ? (Text.fillYear(S.head) || 'Aa').slice(0, 14) : 'Aa Бб';
    for (const f of Fonts.fontsFor(S.locale)) {
        if (f.kind === 'system' && role === 'head') continue;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'gc-font' + (blk && blk.font === f.id ? ' on' : '');
        b.dataset.font = f.id;
        b.title = f.label;
        b.textContent = role === 'head' ? sample : f.label;
        b.style.fontFamily = f.kind === 'system' ? f.css : '"' + f.family + '", ' + f.fb;
        if (f.style === 'italic') b.style.fontStyle = 'italic';
        if (f.weight) b.style.fontWeight = f.weight;
        b.addEventListener('click', () => {
            const t = S.doc.texts.find((x) => x.role === role);
            if (!t) return;
            t.font = f.id;
            if (role === 'head') { const nb = nameBlock(); if (nb && nb.font === t.font) { /* уже совпадает */ } }
            root.querySelectorAll('.gc-font').forEach((x) => x.classList.toggle('on', x === b));
            Fonts.load(f.id, S.locale).then(() => docChanged(false));
            docChanged(false);
            funnel('font', { role, font: f.id });
        });
        root.appendChild(b);
    }
}

const BG_PRESETS = [
    { kind: 'velvet', tint: 'burgundy' }, { kind: 'velvet', tint: 'navy' }, { kind: 'velvet', tint: 'plum' },
    { kind: 'velvet', tint: 'emerald' }, { kind: 'velvet', tint: 'black' }, { kind: 'velvet', tint: 'chocolate' },
    { kind: 'dawn', tint: 'peach' }, { kind: 'dawn', tint: 'gold' }, { kind: 'dawn', tint: 'lilac' },
    { kind: 'pastel', tint: 'candy' }, { kind: 'pastel', tint: 'sky' }, { kind: 'pastel', tint: 'peach' },
    { kind: 'paper', tint: 'cream' }, { kind: 'paper', tint: 'blush' }, { kind: 'paper', tint: 'sage' },
    { kind: 'autumn' }, { kind: 'winter' }, { kind: 'christmas' }, { kind: 'starry' }, { kind: 'sky' },
    { kind: 'sea' }, { kind: 'spring' }, { kind: 'champagne' }, { kind: 'candle' }, { kind: 'neon' },
    { kind: 'sunburst', tint: 'sun' }, { kind: 'sunburst', tint: 'candy' }, { kind: 'sunburst', tint: 'sky' },
];

function renderBgs() {
    const root = $('gcBgs');
    if (root.childElementCount) { markBg(); return; }
    const names = (C.ui && C.ui.bgs) || {};
    for (const p of BG_PRESETS) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'gc-bg';
        b.dataset.bg = p.kind + '|' + (p.tint || '');
        b.title = names[p.kind] || p.kind;
        b.setAttribute('aria-label', names[p.kind] || p.kind);
        const cv = document.createElement('canvas');
        cv.width = 64; cv.height = 64;
        try {
            const built = buildBackground(p, 64, 64, {});
            const g = cv.getContext('2d');
            g.drawImage(built.base, 0, 0);
            if (built.anim) built.anim(g, 1, 6);
        } catch (e) { /* миниатюра фона не критична */ }
        b.appendChild(cv);
        b.addEventListener('click', () => {
            S.doc.bg = Object.assign({}, p);
            markBg();
            docChanged(false);
            funnel('bg', { bg: b.dataset.bg });
        });
        root.appendChild(b);
    }
    markBg();
}

function markBg() {
    const key = S.doc.bg.kind + '|' + (S.doc.bg.tint || '');
    document.querySelectorAll('.gc-bg').forEach((b) => b.classList.toggle('on', b.dataset.bg === key));
}

function bindStyle() {
    $('gcHeadSize').addEventListener('input', (e) => setTextSize('head', (Number(e.target.value) / 100) * baseSize('head'), true));
    $('gcBodySize').addEventListener('input', (e) => setTextSize('body', (Number(e.target.value) / 100) * baseSize('body'), true));
    const align = (roles) => (v) => {
        for (const r of roles) { const t = textBlock(r); if (t) t.align = ALIGNS.includes(v) ? v : 'center'; }
        docChanged(false);
        if (S.selection) { renderSelBar(S.selection); stage.drawSelection(); }
        funnel('align', { roles: roles.join(','), v });
    };
    bindSeg('gcAlignHead', align(['head', 'name']));
    bindSeg('gcAlignBody', align(['body', 'sign']));
}

/* ── анимация ─────────────────────────────────────────────────────────── */

const PARTICLE_CHOICES = ['none', 'sparkles', 'glitter', 'dust', 'confetti', 'snow', 'flakes', 'petals', 'hearts', 'leaves', 'balloons', 'fireworks', 'stars', 'bokeh', 'bubbles', 'lanterns', 'butterflies'];
const DEFAULT_PAL = { sparkles: 'gold', glitter: 'gold', dust: 'gold', confetti: 'confetti', snow: 'snow', flakes: 'snow', petals: 'rose', hearts: 'hearts', leaves: 'autumn', balloons: 'balloons', fireworks: 'fireworks', stars: 'silver', bokeh: 'bokeh', bubbles: 'bubbles', lanterns: 'lanterns', butterflies: 'butterflies' };

function renderAnim() {
    const pr = $('gcParticles');
    pr.textContent = '';
    const names = (C.ui && C.ui.particles) || {};
    const main = S.doc.particles[0];
    for (const k of PARTICLE_CHOICES) {
        if (k !== 'none' && !PARTICLE_KINDS.includes(k)) continue;
        const b = chip(names[k] || k, (k === 'none' && !main) || (main && main.kind === k));
        b.addEventListener('click', () => {
            if (k === 'none') S.doc.particles.shift();
            else if (main) S.doc.particles[0] = { kind: k, density: main.density || 1, palette: DEFAULT_PAL[k] };
            else S.doc.particles.unshift({ kind: k, density: 1, palette: DEFAULT_PAL[k] });
            renderAnim();
            docChanged(false);
            funnel('particles', { kind: k });
        });
        pr.appendChild(b);
    }
    $('gcDensity').value = Math.round(((main && main.density) || 1) * 100);
    const it = $('gcIntros');
    it.textContent = '';
    const intros = (C.ui && C.ui.intros) || {};
    const cur = headBlock().anim.in;
    for (const k of ['pen', 'type', 'fade', 'rise', 'pop', 'zoom', 'glow', 'none']) {
        const b = chip(intros[k] || k, cur === k);
        b.addEventListener('click', () => {
            for (const t of S.doc.texts) {
                if (t.role === 'head' || t.role === 'name') t.anim.in = k;
                else t.anim.in = k === 'none' ? 'none' : 'fade';
                if (k === 'pen' || k === 'type') t.anim.dur = t.role === 'head' ? 1.9 : 1.2;
                else if (t.anim.dur > 1.3) t.anim.dur = 1.1;
            }
            renderAnim();
            docChanged(true);
        });
        it.appendChild(b);
    }
    const hb = headBlock();
    $('gcSparkles').value = Math.round((hb.fx.sparkles || 0) * 100);
    $('gcGlow').value = Math.round((hb.fx.glow || 0) * 100);
    $('gcShimmer').checked = !!hb.fx.shimmer;
    segValue('gcSpeed', S.doc.loop);
}

function chip(label, on) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'tc-chip' + (on ? ' on' : '');
    b.textContent = label;
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    return b;
}

function bindAnim() {
    $('gcDensity').addEventListener('input', (e) => {
        if (!S.doc.particles[0]) return;
        S.doc.particles[0].density = Number(e.target.value) / 100;
        docChanged(false, true);
    });
    $('gcSparkles').addEventListener('input', (e) => {
        const v = Number(e.target.value) / 100;
        for (const t of S.doc.texts) if (t.role === 'head' || t.role === 'name') { t.fx.sparkles = t.role === 'head' ? v : v * 0.6; t.fx.glitter = v * 0.5; }
        docChanged(false, true);
    });
    $('gcGlow').addEventListener('input', (e) => {
        const v = Number(e.target.value) / 100;
        for (const t of S.doc.texts) if (t.role === 'head' || t.role === 'name') t.fx.glow = t.role === 'head' ? v : v * 0.7;
        docChanged(false, true);
    });
    $('gcShimmer').addEventListener('change', (e) => {
        for (const t of S.doc.texts) if (t.role === 'head' || t.role === 'name') t.fx.shimmer = e.target.checked;
        docChanged(false);
    });
    bindSeg('gcSpeed', (v) => { S.doc.loop = Number(v); docChanged(false); });
}

/* ── украшения ────────────────────────────────────────────────────────── */

const STICKERS = ['cake', 'gift', 'balloon', 'popper', 'confetti', 'sparkles', 'glowstar', 'star', 'crown', 'champagne', 'glasses', 'cupcake', 'shortcake', 'teddy', 'ribbon', 'heart', 'sparkheart', 'heartribbon', 'twohearts', 'kiss', 'letter', 'rose', 'bouquet', 'tulip', 'sunflower', 'sakura', 'hibiscus', 'blossom', 'clover', 'maple', 'fallenleaf', 'pumpkin', 'xmastree', 'snowman', 'snowflake', 'santa', 'bell', 'pinedeco', 'fireworks', 'sparkler', 'moon', 'sun', 'sunface', 'rainbow', 'coffee', 'tea', 'croissant', 'cookie', 'doughnut', 'chocolate', 'candy', 'apple', 'books', 'gradcap', 'medal', 'trophy', 'rosette', 'gem', 'ring', 'wedding', 'dove', 'candle', 'egg', 'hatchingchick', 'babychick', 'babybottle', 'bunny', 'butterfly', 'cat', 'dog', 'notes', 'house', 'key', 'plant', 'seedling', 'militarymedal', 'fire', 'mosque', 'kaaba', 'starcrescent', 'lantern', 'mooncake', 'diya', 'palm', 'beach', 'cocktail', 'wine', 'airplane', 'lovingface', 'hug', 'pray', 'bandage', 'pill'];

let stickersRendered = false;
function renderStickers() {
    renderExtras();
    renderStickerEdit();
    if (stickersRendered) return;
    stickersRendered = true;
    const root = $('gcStickers');
    for (const id of STICKERS) {
        if (NO_ALCOHOL.has(S.locale) && ALCOHOL_SWAP[id]) continue;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'gc-st';
        b.dataset.id = id;
        b.setAttribute('aria-label', id);
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = '';
        img.width = 48; img.height = 48;
        img.src = '/vendor/fluent-emoji/3d/' + id + '.webp';
        b.appendChild(img);
        b.addEventListener('click', () => addSticker(id));
        root.appendChild(b);
    }
}

/* Куда ставить новую наклейку: по кругу по свободным «местам» у краёв —
   не на надпись и не одна на другую. */
const STICKER_SPOTS = [[0.16, 0.86], [0.84, 0.86], [0.14, 0.14], [0.86, 0.14], [0.5, 0.9], [0.1, 0.5], [0.9, 0.5]];
async function addSticker(id) {
    await ensureStickers([id]);
    const n = S.doc.deco.filter((d) => d.user).length;
    const [x, y] = STICKER_SPOTS[n % STICKER_SPOTS.length];
    S.doc.deco.push({ kind: 'sticker', id, x, y, size: 0.18, anim: 'float', user: true, phase: (n * 0.23) % 1 });
    docChanged(false);
    stage.select({ kind: 'sticker', index: S.doc.deco.length - 1 });
    funnel('sticker_add', { id });
}

function selectedSticker() {
    const s = S.selection;
    return s && s.kind === 'sticker' ? S.doc.deco[s.index] : null;
}

function renderStickerEdit() {
    const d = selectedSticker();
    const box = $('gcStickerEdit');
    box.hidden = !d;
    if (!d) return;
    reflectStickerEdit();
    const root = $('gcStickerAnims');
    root.textContent = '';
    const names = (C.ui && C.ui.sticker_anims) || {};
    for (const k of STICKER_ANIMS) {
        const b = chip(names[k] || k, (d.anim || 'float') === k);
        b.addEventListener('click', () => { d.anim = k; renderStickerEdit(); docChanged(false); });
        root.appendChild(b);
    }
}

/** Ползунки выбранной наклейки — по тому, что сейчас на открытке. */
function reflectStickerEdit() {
    const d = selectedSticker();
    if (!d || $('gcStickerEdit').hidden) return;
    const b = stage.scene.build().deco[S.selection.index];
    $('gcStickerSize').value = Math.round(((b && b.size) || d.size || 0.2) * 100);
    $('gcStickerRot').value = Math.round(d.rot || 0);
    $('gcStickerRotVal').textContent = Math.round(d.rot || 0) + '°';
    $('gcStickerFront').setAttribute('aria-pressed', d.layer === 'front' ? 'true' : 'false');
    $('gcStickerFront').classList.toggle('on', d.layer === 'front');
}

/* ── выбранный объект: размер, поворот, место ─────────────────────────────
   Одни и те же действия приходят с ручек рамки, щипка, панели над объектом,
   ползунков вкладок и клавиатуры — поэтому живут здесь одним набором. */

/** Главная наклейка шаблона, которую сдвинули рукой, остаётся там, где её
 *  оставили, и дальше стоит сама (free); место под неё в раскладке остаётся
 *  за ней, поэтому надписи не перескакивают. Размер и поворот её на месте
 *  не отвязывают: размер на месте — это масштаб раскладки (sizeModel). */
function freeHero(d, i) {
    if (!d || d.role !== 'hero' || d.free) return;
    const b = stage.scene.build().deco[i];
    if (b) { d.x = b.x; d.y = b.y; d.size = b.size; }
    d.free = true;
}

/**
 * Что значит «размер» у выбранного: надпись — её кегль (1 = подобран по
 * месту), наклейка — её доля кадра, главная наклейка на своём месте —
 * масштаб раскладки (надписи подвинутся и не окажутся под ней), фото —
 * его размер. → {get, set, min, max} | null
 */
function sizeModel(hit) {
    if (!hit || !S.doc) return null;
    if (hit.kind === 'text') {
        const t = S.doc.texts.find((x) => x.role === hit.role);
        return t ? { get: () => t.size || 1, set: (v) => { t.size = v; }, min: 0.4, max: 2.2 } : null;
    }
    if (hit.kind === 'sticker') {
        const d = S.doc.deco[hit.index];
        if (!d) return null;
        if (d.role === 'hero' && !d.free) {
            const L = S.doc.layout;
            return { get: () => L.heroScale || 1, set: (v) => { L.heroScale = v; }, min: 0.5, max: 1.8 };
        }
        return { get: () => d.size || 0.2, set: (v) => { d.size = v; }, min: 0.04, max: 1.2 };
    }
    if (hit.kind === 'photo' && S.doc.photo) {
        const p = S.doc.photo;
        return { get: () => p.size || 1, set: (v) => { p.size = v; }, min: 0.4, max: 1.8 };
    }
    return null;
}

/** Правка выбранного прямо в документе: перерисовать, подвинуть рамку и
 *  панели. live — жест ещё идёт (работа сохраняется, шагом не считается). */
function afterSelEdit(live) {
    stage.scene.dirty = true;
    stage.kick();
    stage.drawSelection();
    reflectSel();
    touch(!live);
}

function scaleSel(hit, factor) {
    const m = sizeModel(hit);
    if (!m) return false;
    const v = clamp(m.get() * factor, m.min, m.max);
    if (Math.abs(v - m.get()) < 1e-4) return false;
    m.set(Math.round(v * 1000) / 1000);
    afterSelEdit(false);
    return true;
}

let sizeBase = null;
function onResize(hit, factor, phase) {
    const m = sizeModel(hit);
    if (!m) return;
    if (phase === 'start') { sizeBase = m.get(); return; }
    if (sizeBase == null) return;
    if (phase === 'cancel') { m.set(sizeBase); sizeBase = null; afterSelEdit(false); return; }
    m.set(Math.round(clamp(sizeBase * factor, m.min, m.max) * 1000) / 1000);
    if (phase === 'end') { sizeBase = null; afterSelEdit(false); funnel('resize', { kind: hit.kind }); return; }
    afterSelEdit(true);
}

function normDeg(a) {
    let x = a;
    while (x > 180) x -= 360;
    while (x <= -180) x += 360;
    return x;
}

/** Повернуть наклейку на угол от начала жеста. Прямые углы «прилипают» —
 *  наклейку легко вернуть ровно; с Shift — шаг 15°. */
let rotBase = null;
function onRotate(hit, delta, phase, fine) {
    const d = hit && hit.kind === 'sticker' ? S.doc.deco[hit.index] : null;
    if (!d) return;
    if (phase === 'start') { rotBase = d.rot || 0; return; }
    if (rotBase == null) return;
    if (phase === 'cancel') { d.rot = rotBase; rotBase = null; afterSelEdit(false); return; }
    let r = normDeg(rotBase + delta);
    const step = fine ? 15 : 90;
    const near = normDeg(Math.round(r / step) * step);
    if (fine || Math.abs(normDeg(r - near)) < 5) r = near;
    d.rot = Math.round(r);
    if (phase === 'end') { rotBase = null; afterSelEdit(false); funnel('rotate', {}); return; }
    afterSelEdit(true);
}

function rotateStep(deg) {
    const d = selectedSticker();
    if (!d) return;
    d.rot = normDeg(Math.round((d.rot || 0) / 15) * 15 + deg);
    afterSelEdit(false);
}

/** Центр выбранного на открытке (доли кадра). */
function centerOf(hit) {
    const Ls = stage.scene.build();
    if (hit.kind === 'sticker') { const b = Ls.deco[hit.index]; return b ? { x: b.x, y: b.y } : null; }
    if (hit.kind === 'text') {
        const t = S.doc.texts.find((x) => x.role === hit.role);
        if (t && t.pos) return { x: t.pos.x, y: t.pos.y };
        const box = Ls.lay.texts[hit.role];
        return box ? { x: box.x + box.w / 2, y: box.y + box.h / 2 } : null;
    }
    if (hit.kind === 'photo' && Ls.photo) {
        const p = S.doc.photo;
        if (p && p.pos) return { x: p.pos.x, y: p.pos.y };
        return { x: Ls.photo.cx / stage.scene.W, y: Ls.photo.cy / stage.scene.H };
    }
    return null;
}

function placeAt(hit, x, y) {
    if (hit.kind === 'sticker') {
        const d = S.doc.deco[hit.index];
        if (!d) return;
        freeHero(d, hit.index);
        d.x = clamp(x, -0.1, 1.1);
        d.y = clamp(y, -0.1, 1.1);
    } else if (hit.kind === 'text') {
        const t = S.doc.texts.find((v) => v.role === hit.role);
        if (t) t.pos = { x: clamp(x, 0, 1), y: clamp(y, 0, 1) };
    } else if (hit.kind === 'photo' && S.doc.photo) {
        S.doc.photo.pos = { x: clamp(x, 0, 1), y: clamp(y, 0, 1) };
    }
}

/** «Вернуть как было» для выбранного: на место раскладки и в свой размер. */
function resetSel() {
    const hit = S.selection;
    if (!hit) return;
    if (hit.kind === 'text') {
        const t = S.doc.texts.find((x) => x.role === hit.role);
        if (t) { t.pos = null; t.size = baseSize(t.role); }
    } else if (hit.kind === 'photo' && S.doc.photo) {
        S.doc.photo.pos = null;
        S.doc.photo.size = 1;
    } else if (hit.kind === 'sticker') {
        const d = S.doc.deco[hit.index];
        if (d && d.role === 'hero') { delete d.free; S.doc.layout.heroScale = 1; }
        if (d) d.rot = 0;
    }
    afterSelEdit(false);
    funnel('sel_reset', { kind: hit.kind });
}

const ALIGNS = ['center', 'left', 'right'];
function cycleAlign() {
    const hit = S.selection;
    const t = hit && hit.kind === 'text' ? S.doc.texts.find((x) => x.role === hit.role) : null;
    if (!t) return;
    t.align = ALIGNS[(ALIGNS.indexOf(t.align || 'center') + 1) % ALIGNS.length];
    afterSelEdit(false);
    renderSelBar(hit);
    stage.drawSelection();
}

function flipSticker() {
    const d = selectedSticker();
    if (!d) return;
    d.flip = !d.flip;
    afterSelEdit(false);
}

function toggleLayer() {
    const d = selectedSticker();
    if (!d) return;
    if (d.layer === 'front') delete d.layer; else d.layer = 'front';
    afterSelEdit(false);
}

function duplicateSticker() {
    const s = S.selection;
    const d = selectedSticker();
    if (!d) return;
    const b = stage.scene.build().deco[s.index] || d;
    const copy = Object.assign({}, d, { x: clamp(b.x + 0.06, 0.05, 0.95), y: clamp(b.y + 0.06, 0.05, 0.95), size: b.size, user: true });
    delete copy.role;
    delete copy.free;
    S.doc.deco.push(copy);
    docChanged(false);
    stage.select({ kind: 'sticker', index: S.doc.deco.length - 1 });
    funnel('sticker_copy', { id: d.id });
}

function removeSticker() {
    const s = S.selection;
    if (!s || s.kind !== 'sticker') return;
    S.doc.deco.splice(s.index, 1);
    stage.select(null);
    docChanged(false);
}

/** Стрелки — сдвиг (с Shift — крупнее), ± — размер, Delete — убрать наклейку. */
function onKey(e, hit) {
    const dirs = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (dirs[e.key]) {
        const c = centerOf(hit);
        if (!c) return false;
        const st = e.shiftKey ? 0.05 : 0.01;
        placeAt(hit, c.x + dirs[e.key][0] * st, c.y + dirs[e.key][1] * st);
        afterSelEdit(false);
        return true;
    }
    if (e.key === '+' || e.key === '=') { scaleSel(hit, 1.06); return true; }
    if (e.key === '-' || e.key === '_') { scaleSel(hit, 1 / 1.06); return true; }
    if ((e.key === 'Delete' || e.key === 'Backspace') && hit.kind === 'sticker') { removeSticker(); return true; }
    return false;
}

/* ── панель над выбранным ─────────────────────────────────────────────── */

const ICONS = {
    minus: '<path d="M5 12h14"/>',
    plus: '<path d="M12 5v14M5 12h14"/>',
    left: '<path d="M4 6h16M4 10h10M4 14h16M4 18h10"/>',
    center: '<path d="M4 6h16M7 10h10M4 14h16M7 18h10"/>',
    right: '<path d="M4 6h16M10 10h10M4 14h16M10 18h10"/>',
    reset: '<path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3"/><path d="M4 4.5V9h4.5"/>',
    edit: '<path d="M4 20h4L19 9l-4-4L4 16v4z"/><path d="M13.5 6.5l4 4"/>',
    rotate: '<path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3"/><path d="M20 4.5V9h-4.5"/>',
    flip: '<path d="M12 3v18"/><path d="M8.5 7.5L4 12l4.5 4.5zM15.5 7.5L20 12l-4.5 4.5z"/>',
    copy: '<rect x="8.5" y="8.5" width="11" height="11" rx="2"/><path d="M15.5 8.5V6a1.5 1.5 0 0 0-1.5-1.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h2.5"/>',
    front: '<path d="M12 4l8 4.5-8 4.5-8-4.5z"/><path d="M4 13l8 4.5 8-4.5"/>',
    trash: '<path d="M4 7h16M9.5 7V4.5h5V7M6.5 7l1 12.5h9l1-12.5"/>',
    photo: '<rect x="3.5" y="5.5" width="17" height="13" rx="2"/><circle cx="9" cy="11" r="1.8"/><path d="M20.5 16.5l-5.5-5.5-8 7.5"/>',
    undo: '<path d="M9 14L4.5 9.5 9 5"/><path d="M4.5 9.5h10a5 5 0 0 1 0 10H11"/>',
    redo: '<path d="M15 14l4.5-4.5L15 5"/><path d="M19.5 9.5h-10a5 5 0 0 0 0 10H13"/>',
};

function icon(name) {
    return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + ICONS[name] + '</svg>';
}

/** Кнопка с повтором при удержании (±, поворот): шаг на нажатие — общий
 *  примитив, свой click — путь клавиатуры. */
function stepButton(b, fn) {
    if (window.__holdRepeat) window.__holdRepeat(b, () => { fn(); });
    b.addEventListener('click', () => fn());
    return b;
}

function tbButton(name, label, fn, opts) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gc-tb-b' + (opts && opts.cls ? ' ' + opts.cls : '');
    b.innerHTML = icon(name);
    b.setAttribute('aria-label', label);
    b.title = label;
    if (opts && opts.hold) stepButton(b, fn);
    else b.addEventListener('click', fn);
    return b;
}

function renderSelBar(sel) {
    const bar = $('gcSelBar');
    bar.textContent = '';
    if (!sel) { bar.hidden = true; return; }
    const sep = () => { const s = document.createElement('span'); s.className = 'gc-tb-sep'; s.setAttribute('aria-hidden', 'true'); bar.appendChild(s); };
    bar.appendChild(tbButton('minus', u('size_smaller'), () => scaleSel(S.selection, 1 / 1.08), { hold: true }));
    if (sel.kind === 'text') {
        const val = document.createElement('span');
        val.className = 'gc-tb-val';
        val.id = 'gcSelSize';
        bar.appendChild(val);
    }
    bar.appendChild(tbButton('plus', u('size_bigger'), () => scaleSel(S.selection, 1.08), { hold: true }));
    sep();
    if (sel.kind === 'text') {
        const t = S.doc.texts.find((x) => x.role === sel.role);
        const al = (t && t.align) || 'center';
        const ab = tbButton(al, u('align') + ': ' + u('align_' + al), cycleAlign);
        // «left» у надписи — начало строки: справа налево (ar) значок зеркалится.
        ab.dataset.al = al;
        bar.appendChild(ab);
        bar.appendChild(tbButton('reset', u('sel_reset'), resetSel));
        bar.appendChild(tbButton('edit', u('sel_edit_text'), () => focusTextField(sel.role)));
    } else if (sel.kind === 'sticker') {
        bar.appendChild(tbButton('rotate', u('deco_rotate'), () => rotateStep(15), { hold: true }));
        bar.appendChild(tbButton('flip', u('deco_flip'), flipSticker, { cls: 'gc-tb-opt' }));
        bar.appendChild(tbButton('copy', u('deco_duplicate'), duplicateSticker));
        const lb = tbButton('front', u('deco_front'), toggleLayer, { cls: 'gc-tb-opt' });
        lb.id = 'gcSelFront';
        bar.appendChild(lb);
        sep();
        bar.appendChild(tbButton('trash', u('deco_remove'), removeSticker, { cls: 'danger' }));
    } else if (sel.kind === 'photo') {
        bar.appendChild(tbButton('reset', u('sel_reset'), resetSel));
        bar.appendChild(tbButton('photo', u('photo_edit'), () => showTab('photo')));
    }
    reflectSel();
}

/** Числа и состояния на панели и во вкладках — по документу. */
function reflectSel() {
    const sel = S.selection;
    const v = $('gcSelSize');
    if (v && sel && sel.kind === 'text') {
        const t = S.doc.texts.find((x) => x.role === sel.role);
        if (t) v.textContent = sizePct(t);
    }
    const fr = $('gcSelFront');
    const d = selectedSticker();
    if (fr && d) { fr.setAttribute('aria-pressed', d.layer === 'front' ? 'true' : 'false'); fr.classList.toggle('on', d.layer === 'front'); }
    reflectStickerEdit();
    reflectTextSizes();
}

function focusTextField(role) {
    showTab('text');
    const id = { head: 'gcHead', name: 'gcName', body: 'gcBody', sign: 'gcSign' }[role];
    const el = id && $(id);
    if (el) { el.focus({ preventScroll: true }); if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' }); }
}

const EXTRAS = [
    { key: 'frame', make: () => ({ kind: 'frame', tone: 'gold', inset: 0.045 }) },
    { key: 'garland', make: () => ({ kind: 'garland', swags: 2, y: 0, sag: 0.08 }) },
    { key: 'bunting', make: () => ({ kind: 'bunting', y: 0, sag: 0.07, swags: 2 }) },
    { key: 'balloons', make: () => ({ kind: 'balloons', side: 'both', count: 4, y: 0.34 }) },
    { key: 'ornaments', make: () => ({ kind: 'ornaments' }) },
];

function renderExtras() {
    const root = $('gcExtras');
    root.textContent = '';
    for (const x of EXTRAS) {
        const has = S.doc.deco.some((d) => d.kind === x.key);
        const b = chip(u('deco_' + x.key), has);
        b.addEventListener('click', () => {
            // Индексы наклеек сдвинутся — выбранная перестала бы быть собой.
            if (S.selection && S.selection.kind === 'sticker') stage.select(null);
            if (has) S.doc.deco = S.doc.deco.filter((d) => d.kind !== x.key);
            else S.doc.deco.unshift(x.make());
            renderExtras();
            docChanged(true);
        });
        root.appendChild(b);
    }
}

function bindDeco() {
    // Ползунок показывает видимый размер (долю короткой стороны) и для
    // главной наклейки шаблона тоже: раньше он писал в d.size, которого её
    // раскладка не читает, и звезда «День отца» не менялась вовсе.
    $('gcStickerSize').addEventListener('input', (e) => {
        const s = S.selection;
        const d = selectedSticker();
        const b = d && stage.scene.build().deco[s.index];
        if (!b || !b.size) return;
        const m = sizeModel(s);
        const want = Number(e.target.value) / 100;
        m.set(Math.round(clamp(m.get() * (want / b.size), m.min, m.max) * 1000) / 1000);
        afterSelEdit(true);
    });
    $('gcStickerRot').addEventListener('input', (e) => {
        const d = selectedSticker();
        if (!d) return;
        d.rot = Number(e.target.value);
        afterSelEdit(true);
    });
    $('gcStickerFlip').addEventListener('click', flipSticker);
    $('gcStickerCopy').addEventListener('click', duplicateSticker);
    $('gcStickerFront').addEventListener('click', toggleLayer);
    $('gcStickerRemove').addEventListener('click', removeSticker);
}

/* ── фото ─────────────────────────────────────────────────────────────── */

const SHAPES = ['circle', 'heart', 'arch', 'rounded', 'polaroid', 'full'];
const BORDERS = ['gold', 'silver', 'rose', 'white', 'none'];

/**
 * Фото на открытку. restore — открытие черновика: форма, рамка, масштаб уже
 * в документе, их не трогать; правкой это не считается.
 * @returns {Promise<boolean>}
 */
async function onPhotoFile(file, restore) {
    if (!file) return false;
    let bmp = null;
    try {
        bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) {
        try {
            if (window.HeicConvert && /heic|heif/i.test(file.type + file.name)) {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.src = url;
                await img.decode();
                bmp = await createImageBitmap(img);
                URL.revokeObjectURL(url);
            }
        } catch (_) { bmp = null; }
    }
    if (!bmp) {
        if (!restore && window.__toast) window.__toast(u('photo_decode_fail'), 'error');
        return false;
    }
    // Фото больше 2048 px по длинной стороне уменьшаем: открытке больше не
    // нужно, а память телефона — не резиновая.
    const k = Math.min(1, 2048 / Math.max(bmp.width, bmp.height));
    if (k < 1) {
        const c = makeCanvas(Math.round(bmp.width * k), Math.round(bmp.height * k));
        c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
        bmp = await createImageBitmap(c);
    }
    S.photo = bmp;
    S.photoFile = file;
    if (!restore || !S.photoId) S.photoId = 'p' + rid();
    S.doc.photo = S.doc.photo || { shape: 'circle', border: 'gold', zoom: 1, panX: 0, panY: 0, glow: 0.5, size: 1 };
    if (S.doc.layout.hero === 'none') S.doc.layout.hero = 'top';
    stage.setPhoto(bmp);
    $('gcPhotoOpts').hidden = false;
    renderPhotoOpts();
    if (restore) return true;
    docChanged(true);
    funnel('photo_add', { w: bmp.width, h: bmp.height });
    return true;
}

function renderPhotoOpts() {
    const p = S.doc.photo;
    if (!p) return;
    const shapes = $('gcShapes'); shapes.textContent = '';
    const sn = (C.ui && C.ui.shapes) || {};
    const isFull = S.doc.bg.kind === 'photo';
    for (const k of SHAPES) {
        const b = chip(sn[k] || k, isFull ? k === 'full' : p.shape === k);
        b.addEventListener('click', () => {
            if (k === 'full') {
                if (S.doc.bg.kind !== 'photo') S.prevBg = S.doc.bg;
                S.doc.bg = { kind: 'photo', dim: 0.15 };
                S.doc.photo.hidden = true;
            } else {
                if (S.doc.bg.kind === 'photo') S.doc.bg = S.prevBg || { kind: 'velvet', tint: 'plum' };
                S.doc.photo.shape = k;
                S.doc.photo.hidden = false;
            }
            renderPhotoOpts();
            docChanged(false);
        });
        shapes.appendChild(b);
    }
    const borders = $('gcBorders'); borders.textContent = '';
    const bn = (C.ui && C.ui.borders) || {};
    for (const k of BORDERS) {
        const b = chip(bn[k] || k, p.border === k);
        b.addEventListener('click', () => { S.doc.photo.border = k; renderPhotoOpts(); docChanged(false); });
        borders.appendChild(b);
    }
    $('gcPhotoZoom').value = Math.round((p.zoom || 1) * 100);
    $('gcPhotoSize').value = Math.round((p.size || 1) * 100);
}

function bindPhoto() {
    const zone = $('gcPhotoDrop');
    const onFile = (e) => { const f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0])); if (f) onPhotoFile(f); };
    if (window.__bindUploaderFile) window.__bindUploaderFile(zone, (d) => onPhotoFile(d && (d.file || (d.files && d.files[0]))));
    else if (zone) zone.addEventListener('tool-uploader:file', onFile);
    if (zone) zone.addEventListener('tool-uploader:clear', () => { if (!zoneQuiet && S.photo) removePhoto(); });
    $('gcPhotoZoom').addEventListener('input', (e) => { if (!S.doc.photo) return; S.doc.photo.zoom = Number(e.target.value) / 100; docChanged(false, true); });
    $('gcPhotoSize').addEventListener('input', (e) => { if (!S.doc.photo) return; S.doc.photo.size = Number(e.target.value) / 100; docChanged(false, true); });
    $('gcPhotoRemove').addEventListener('click', () => {
        removePhoto();
        clearZone('gcPhotoDrop');
    });
}

let zoneQuiet = false;
function clearZone(id) {
    zoneQuiet = true;
    try { if (window.ToolUploader) window.ToolUploader.clear(id); } catch (e) { /* зоны может не быть */ }
    zoneQuiet = false;
}

function removePhoto(opts) {
    if (stage && S.selection && S.selection.kind === 'photo') stage.select(null);
    S.photo = null;
    S.photoFile = null;
    S.photoId = '';
    if (S.doc.bg.kind === 'photo') S.doc.bg = S.prevBg || { kind: 'velvet', tint: 'plum' };
    S.doc.photo = null;
    stage.setPhoto(null);
    $('gcPhotoOpts').hidden = true;
    if (!(opts && opts.quiet)) docChanged(false);
}

/* ── перетаскивание на открытке ───────────────────────────────────────── */

/* Протяжка: место — от центра в начале жеста плюс путь пальца (а не сумма
   шагов), поэтому «прилипание» к середине открытки не копит ошибку. Середина
   по горизонтали и вертикали притягивает в пределах SNAP и показывает
   направляющую. Отменённое касание (pointercancel) возвращает как было. */
const SNAP = 0.012;
let dragSess = null;

function sameSel(a, b) {
    if (!a || !b || a.kind !== b.kind) return false;
    if (a.kind === 'text') return a.role === b.role;
    if (a.kind === 'sticker') return a.index === b.index;
    return true;
}

function onDrag(hit, dx, dy, p, pStart) {
    if (!dragSess || !sameSel(dragSess.hit, hit)) {
        const c = centerOf(hit);
        if (!c) return;
        const d = hit.kind === 'sticker' ? S.doc.deco[hit.index] : null;
        dragSess = { hit, c0: c, wasFree: !!(d && d.free) };
    }
    const from = pStart || p;
    let x = dragSess.c0.x + (p.x - from.x);
    let y = dragSess.c0.y + (p.y - from.y);
    const sv = Math.abs(x - 0.5) < SNAP, sh = Math.abs(y - 0.5) < SNAP;
    if (sv) x = 0.5;
    if (sh) y = 0.5;
    placeAt(hit, x, y);
    stage.showGuides(sv, sh);
    stage.scene.dirty = true;
    stage.kick();
    stage.drawSelection();
}

function onDragEnd(hit, cancelled) {
    const sess = dragSess;
    dragSess = null;
    if (cancelled && sess) {
        placeAt(hit, sess.c0.x, sess.c0.y);
        const d = hit.kind === 'sticker' ? S.doc.deco[hit.index] : null;
        if (d && !sess.wasFree) delete d.free;
        afterSelEdit(false);
    } else if (!cancelled) {
        touch(true);
        reflectSel();
    }
    funnel('drag', { kind: hit.kind, cancelled: !!cancelled });
}

function onTap(hit) {
    if (!hit) { renderStickerEdit(); return; }
    if (hit.kind === 'text') {
        // Мышью — сразу в поле. Пальцем — только выбор: клавиатура телефона
        // закрыла бы открытку, а надпись часто выбирают ради размера и
        // места; писать — кнопкой «Изменить текст» на панели.
        const fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
        if (fine) focusTextField(hit.role);
        else showTab('text');
    } else if (hit.kind === 'sticker') {
        showTab('deco');
        renderStickerEdit();
    } else if (hit.kind === 'photo') {
        showTab('photo');
    }
}

function clamp(x, a, b) { return x < a ? a : x > b ? b : x; }

/* ── формат, звук, пауза ──────────────────────────────────────────────── */

function bindTransport() {
    $('gcFmt').addEventListener('click', (e) => {
        const b = e.target.closest('[data-fmt]');
        if (!b || !FORMATS[b.dataset.fmt]) return;
        document.querySelectorAll('#gcFmt [data-fmt]').forEach((x) => x.classList.toggle('on', x === b));
        S.doc.fmt = b.dataset.fmt;
        settings.save(SLUG, 'fmt', S.doc.fmt);
        stage.select(null);
        docChanged(true);
        funnel('format', { fmt: S.doc.fmt });
    });
    $('gcReplay').addEventListener('click', () => { stage.replay(); music.restart(); });
    $('gcPause').addEventListener('click', () => {
        const p = !stage.paused;
        stage.setPaused(p);
        music.setPaused(p);
        $('gcPause').setAttribute('aria-pressed', p ? 'true' : 'false');
        $('gcPause').textContent = p ? '▶' : '❚❚';
        $('gcPause').title = p ? u('resume') : u('pause');
    });
    $('gcSound').addEventListener('click', async () => {
        const on = !music.soundOn;
        await music.setSound(on);
        $('gcSound').textContent = on ? '🔊' : '🔇';
        $('gcSound').setAttribute('aria-pressed', on ? 'true' : 'false');
        $('gcSound').title = on ? u('sound_off') : u('sound_on');
        if (on) { stage.replay(); music.restart(); }
    });
}

function reflectPanels() {
    document.querySelectorAll('#gcFmt [data-fmt]').forEach((x) => x.classList.toggle('on', x.dataset.fmt === S.doc.fmt));
    if (!$('gcHeadSize')) return;
    reflectTextSizes();
    const pane = document.querySelector('.gc-pane:not([hidden])');
    const tab = pane && pane.dataset.panel;
    if (tab === 'style') { renderFills('gcFills', 'head'); renderFills('gcFillsBody', 'body'); renderFonts('gcFonts', 'head'); renderFonts('gcFontsBody', 'body'); markBg(); reflectLayout(); }
    if (tab === 'anim') renderAnim();
    if (tab === 'deco') { renderExtras(); renderStickerEdit(); }
    if (tab === 'photo') renderPhotoOpts();
}

/* ── «Мои открытки»: работа переживает крах, F5 и закрытую вкладку ───── */

let drafts = null;
let arrivedWith = false;   // страница открылась с чужой открыткой или поводом из ссылки

/** Правка работы. counted — законченный шаг (набор, выбор, перенос), а не
 *  ползунок в движении: только он начинает новую работу (и шаг отмены). */
function touch(counted) {
    if (drafts) drafts.touch(counted !== false);
    if (counted !== false) histSoon();
}

/* ── отмена и повтор ──────────────────────────────────────────────────────
   Снимок — то, что человек меняет руками: документ открытки и тексты полей.
   Фото, своя песня и голос — байты, их в снимке нет: отмена возвращает
   рамку, форму и место фото, но не удалённый файл. Частые шаги (набор,
   ±, стрелки) склеиваются в один — снимок пишется через 400 мс тишины. */
const hist = { stack: [], at: -1, timer: 0, quiet: false };

function histState() {
    if (!S.doc) return null;
    return JSON.stringify({
        doc: S.doc, occ: S.occ, tpl: S.tplId, headIdx: S.headIdx,
        head: S.head, headUser: S.headUser, body: S.body, bodyUser: S.bodyUser, bodySeed: S.bodySeed,
        name: S.name, salute: S.salute, gender: S.gender, sign: S.sign, show: S.show,
    });
}

function histCommit() {
    clearTimeout(hist.timer);
    hist.timer = 0;
    if (hist.quiet) return;
    const s = histState();
    if (!s || hist.stack[hist.at] === s) return;
    hist.stack.length = hist.at + 1;
    hist.stack.push(s);
    if (hist.stack.length > 80) hist.stack.shift();
    hist.at = hist.stack.length - 1;
    reflectHist();
}

function histSoon() {
    if (hist.quiet) return;
    clearTimeout(hist.timer);
    hist.timer = setTimeout(histCommit, 400);
}

/** Новая работа (другая открытка, черновик, «Новая открытка») — своя история. */
function histReset() {
    clearTimeout(hist.timer);
    hist.stack = [];
    hist.at = -1;
    histCommit();
}

function histGo(step) {
    // Недописанный шаг (набор в последние 400 мс) — сначала в историю.
    if (hist.timer) histCommit();
    const i = hist.at + step;
    if (i < 0 || i >= hist.stack.length) return;
    hist.at = i;
    const st = JSON.parse(hist.stack[i]);
    hist.quiet = true;
    try {
        stage.select(null);
        const occChanged = st.occ !== S.occ;
        const tplChanged = st.tpl !== S.tplId;
        Object.assign(S, {
            occ: st.occ, tplId: st.tpl, headIdx: st.headIdx || 0,
            head: st.head, headUser: !!st.headUser, body: st.body, bodyUser: !!st.bodyUser,
            bodySeed: typeof st.bodySeed === 'number' ? st.bodySeed : null,
            name: st.name, salute: st.salute, gender: st.gender === 'male' ? 'male' : 'female', sign: st.sign,
            show: Object.assign({ head: true, name: true, body: true, sign: true }, st.show || {}),
        });
        const doc = normalizeDoc(st.doc);
        // Файла фото уже нет — снимок с фото не может его вернуть.
        if (!S.photo) { doc.photo = null; if (doc.bg.kind === 'photo') doc.bg = S.prevBg || { kind: 'velvet', tint: 'plum' }; }
        S.doc = doc;
        renderSaluteOptions();
        fillTextFields();
        segValue('gcGender', S.gender);
        applyDoc(false);
        if (occChanged) renderOccStrip();
        if (occChanged || tplChanged) renderTemplates();
        updateNameProof();
    } finally {
        hist.quiet = false;
        clearTimeout(hist.timer);
        hist.timer = 0;
    }
    // Снимок после normalizeDoc — тот, с которым сравнится следующая правка:
    // иначе разница в нормализации сама «правила» бы работу и стирала повтор.
    const now = histState();
    if (now) hist.stack[hist.at] = now;
    reflectHist();
    funnel(step < 0 ? 'undo' : 'redo', {});
}

function reflectHist() {
    const un = $('gcUndo'), re = $('gcRedo');
    if (un) un.disabled = hist.at <= 0;
    if (re) re.disabled = hist.at >= hist.stack.length - 1;
}

function bindHistory() {
    $('gcUndo').innerHTML = icon('undo');
    $('gcRedo').innerHTML = icon('redo');
    $('gcUndo').addEventListener('click', () => histGo(-1));
    $('gcRedo').addEventListener('click', () => histGo(1));
    // Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y — когда фокус не в поле ввода: там
    // работает своя отмена набора у браузера.
    document.addEventListener('keydown', (e) => {
        if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
        const el = e.target;
        if (el && el.closest && (el.closest('input, textarea, select, [contenteditable="true"]') || !el.closest('#gcApp') && el !== document.body)) return;
        const k = e.key.toLowerCase();
        if (k === 'z' && !e.shiftKey) { e.preventDefault(); histGo(-1); }
        else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); histGo(1); }
    });
    reflectHist();
}

function draftLabel() {
    const occ = Text.occStrings(S.occ).label;
    const nm = String(S.name || '').trim();
    return (nm ? occ + ' — ' + nm : Text.fillYear(S.head) || occ).slice(0, 90);
}

/* Миниатюра в списке — та же открытка целиком (после «входа»), нарисованная
   тем же движком в маленьком кадре. Кэши шрифтов и наклеек общие со сценой,
   поэтому кадр собирается синхронно, в момент записи. */
const previewScene = new Scene({ locale: S.locale, rtl: S.rtl, still: true });
function draftPreview() {
    try {
        const { W, H } = frameSize(S.doc.fmt, 132);
        previewScene.setSize(W, H, 1);
        previewScene.setDoc(S.doc);
        previewScene.setPhoto(S.photo);
        previewScene.dirty = true;
        // Обычный <canvas>, а не makeCanvas: у OffscreenCanvas нет toDataURL.
        const c = document.createElement('canvas');
        c.width = W; c.height = H;
        const L = S.doc.loop || 6;
        previewScene.draw(c.getContext('2d'), Math.min(L * 0.4, 2.4), { intro: false });
        return c.toDataURL('image/jpeg', 0.72);
    } catch (e) { return ''; }
}

function draftPayload() {
    if (!S.doc) return null;
    const files = [];
    const at = {};
    if (S.photo && S.photoFile) { at.photo = files.length; files.push(S.photoFile); }
    const own = music.ownFile();
    if (own) { at.own = files.length; files.push(own); }
    const voice = music.voiceBytes();
    if (voice) { at.voice = files.length; files.push(voice); }
    return {
        state: {
            v: 1, occ: S.occ, tpl: S.tplId, headIdx: S.headIdx,
            head: S.head, headUser: S.headUser, body: S.body, bodyUser: S.bodyUser, bodySeed: S.bodySeed,
            name: S.name, salute: S.salute, gender: S.gender, sign: S.sign, show: Object.assign({}, S.show),
            tone: S.tone, form: S.form, len: S.len,
            doc: JSON.parse(JSON.stringify(S.doc)), prevBg: S.prevBg || null,
            files: at, photoId: S.photoId, voiceKey: music.voiceId(),
            ownName: own ? own.name : '',
        },
        files,
        label: draftLabel(),
        preview: draftPreview(),
    };
}

/** Вернуть сохранённую открытку на страницу. → что из файлов не вернулось. */
async function applyDraft(d) {
    const st = d.state || {};
    const files = d.files || [];
    if (!st.doc || !OCCASIONS[st.occ]) throw new Error('draft: no card');
    stage.select(null);
    S.occ = OCC_KEYS.includes(st.occ) ? st.occ : OCC_KEYS[0];
    S.tplId = st.tpl || null;
    S.headIdx = st.headIdx || 0;
    S.head = String(st.head || ''); S.headUser = !!st.headUser;
    S.body = String(st.body || ''); S.bodyUser = !!st.bodyUser;
    S.bodySeed = typeof st.bodySeed === 'number' ? st.bodySeed : null;
    S.name = String(st.name || '');
    S.salute = String(st.salute || '');
    if (st.gender === 'male' || st.gender === 'female') S.gender = st.gender;
    S.sign = String(st.sign || '');
    S.show = Object.assign({ head: true, name: true, body: true, sign: true }, st.show || {});
    S.tone = st.tone || 'warm'; S.form = st.form || 'ty'; S.len = st.len || 'short';
    S.prevBg = st.prevBg || null;
    S.doc = normalizeDoc(st.doc);
    const at = st.files || {};
    const missing = [];

    removePhoto({ quiet: true });
    clearZone('gcPhotoDrop');
    S.photoId = st.photoId || '';
    if (at.photo != null) {
        const f = files[at.photo];
        const ok = f ? await onPhotoFile(f, true) : false;
        if (!ok) { missing.push((f && f.name) || 'photo'); S.doc.photo = null; if (S.doc.bg.kind === 'photo') S.doc.bg = S.prevBg || { kind: 'velvet', tint: 'plum' }; }
    } else {
        S.doc.photo = null;
        if (S.doc.bg.kind === 'photo') S.doc.bg = S.prevBg || { kind: 'velvet', tint: 'plum' };
    }

    music.setDoc(S.doc);
    const lost = await music.restore({
        own: at.own != null ? files[at.own] : null, ownWanted: at.own != null,
        voice: at.voice != null ? files[at.voice] : null, voiceWanted: at.voice != null, voiceKey: st.voiceKey,
    });
    missing.push(...lost);

    renderSaluteOptions();
    fillTextFields();
    segValue('gcTone', S.tone); segValue('gcForm', S.form); segValue('gcLen', S.len); segValue('gcGender', S.gender);
    applyDoc(true);
    renderOccStrip();
    renderTemplates();
    updateNameProof();
    histReset();
    return missing;
}

/** «Новая открытка»: тот же повод, чистый лист — без имени, своих слов, фото,
 *  своей песни и голоса. */
function freshCard() {
    stage.select(null);
    S.name = ''; S.head = ''; S.headUser = false; S.body = ''; S.bodyUser = false; S.bodySeed = null;
    S.headIdx = 0; S.wishIdx = 0;
    removePhoto({ quiet: true });
    clearZone('gcPhotoDrop');
    music.reset();
    S.doc = null;
    S.tplId = null;
    setOccasion(S.occ || defaultOccasion(OCC_KEYS, S.locale, new Date()), 'fresh');
    updateNameProof();
    histReset();
}

/* ── знак на бесплатной выгрузке (tiers.watermark_scope, только ru) ──────── */

function watermark() {
    const on = !!(window.__wmShould && window.__WM_SCOPE && window.__wmShould(window.__WM_SCOPE));
    return { on, text: window.__WM_TEXT || C.wmText || '' };
}

function applyWatermark() {
    const w = watermark();
    stage.setWatermark(w.on, w.text);
}

/* ── одна открытка — много имён ───────────────────────────────────────── */

const BATCH_MAX = 50;

function batchNames() {
    const seen = new Set();
    const out = [];
    for (const raw of String($('gcBatchNames').value || '').split(/\r?\n|;/)) {
        const n = raw.trim().replace(/\s+/g, ' ').slice(0, 80);
        if (!n || seen.has(n.toLowerCase())) continue;
        seen.add(n.toLowerCase());
        out.push(n);
    }
    return out;
}

function reflectBatch() {
    const names = batchNames();
    const cnt = $('gcBatchCount');
    const btn = $('gcBatchGo');
    let why = '';
    if (names.length > BATCH_MAX) why = u('batch_max', { n: BATCH_MAX });
    else if (names.length && exporter.kind === 'link') why = u('batch_link_note');
    cnt.textContent = why || (names.length ? u('batch_count', { n: names.length }) : '');
    btn.disabled = !names.length || !!why;
}

/** Открытка для одного имени: строка имени, поздравление с его падежами и
 *  родом (тот же вариант словаря) или своё — с заменой имени. */
async function cardFor(n) {
    const d = JSON.parse(JSON.stringify(S.doc));
    const g = genderOf(n);
    let body = S.body;
    if (!S.bodyUser && S.bodySeed != null) {
        body = (await Text.bodyFor({ occ: S.occ, name: n, gender: g, forcedGender: Text.saluteGender(S.salute), tone: S.tone, form: S.form, length: S.len, seed: S.bodySeed })) || S.body;
    } else if (S.name) {
        body = Text.swapName(S.body, S.name, n, S.locale);
    }
    const set = (role, text) => { const t = d.texts.find((x) => x.role === role); if (t) t.text = text; };
    set('name', Text.nameLine(n, S.salute, S.locale, g, S.occ));
    set('body', body);
    return { name: n, doc: d, key: docKey(d) };
}

function bindBatch() {
    const ta = $('gcBatchNames');
    ta.addEventListener('input', reflectBatch);
    $('gcKinds').addEventListener('click', () => setTimeout(reflectBatch, 0));
    $('gcBatch').addEventListener('toggle', () => {
        if ($('gcBatch').open) { Text.loadFio(); funnel('batch_open', {}); }
    });
    $('gcBatchGo').addEventListener('click', () => {
        const names = batchNames();
        if (!names.length || names.length > BATCH_MAX) return;
        clearTimeout(applyTimer);
        syncTexts();
        // Ключи лимита нужны В САМОМ нажатии (примитив красит кнопку и
        // показывает стену до первого await), а склонение — асинхронное.
        // Поэтому открытки собираются в exporter.batch() после проверки
        // по ключам «на глаз»: строка имени у каждого своя, значит и ключ.
        const quick = names.map((n) => {
            const d = JSON.parse(JSON.stringify(S.doc));
            const t = d.texts.find((x) => x.role === 'name');
            if (t) t.text = Text.nameLine(n, S.salute, S.locale, genderOf(n), S.occ);
            return docKey(d);
        });
        exporter.batch(names, quick, cardFor);
    });
    reflectBatch();
}

/* ── запуск ───────────────────────────────────────────────────────────── */

async function boot() {
    stage = new Stage({
        canvas: $('gcCanvas'), card: $('gcCard'), stage: $('gcStage'), locale: S.locale, rtl: S.rtl,
        onTap, onDrag, onDragEnd, onResize, onRotate, onKey,
        toolbar: $('gcSelBar'),
        labels: { resize: u('sel_resize'), rotate: u('sel_rotate') },
    });
    stage.onSelect = (sel) => { S.selection = sel; renderSelBar(sel); renderStickerEdit(); };
    music = new Music({ cfg: C, stage, locale: S.locale, ui: u, onEdit: touch });
    exporter = new Exporter({
        cfg: C, ui: u, slug: SLUG, getDoc: () => S.doc, getPhoto: () => S.photo,
        getKey: () => cardKey(), music, stage, funnel, encodeLink,
        onDelivered: () => { if (drafts) drafts.exported(); },
        flush: () => { clearTimeout(applyTimer); syncTexts(); stage.setDoc(S.doc); },
        watermark,
    });

    // Суточный лимит видео и GIF — единица «открытка»: ключ живёт, пока
    // человек правит одну открытку, и меняется только на «Новая открытка».
    if (window.__toolLimit && window.__toolLimit.mount) {
        try { window.__toolLimit.mount(SLUG, { button: 'gcSend,gcDownload,gcBatchGo' }); } catch (e) { /* блок лимита сам покажет правила */ }
    }

    // Знак — по тарифу, а тариф приезжает снимком позже загрузки (1,7–3,3 с):
    // до него __wmShould отвечает «гость». Поэтому решение пересматривается
    // на приезде снимка, а выгрузка спрашивает заново в момент сборки.
    applyWatermark();
    document.addEventListener('tier-bootstrap:ready', applyWatermark);

    renderSaluteOptions();
    const q = new URLSearchParams(location.search);
    const want = q.get('occ');
    const tq = q.get('tpl');
    S.occ = want && OCC_KEYS.includes(want) ? want : defaultOccasion(OCC_KEYS, S.locale, new Date());
    S.head = Text.headline(S.occ, 0);
    S.body = Text.shortWish(S.occ, 0);
    rebuildFromTemplate(tq && templateById(tq) ? tq : seasonalFirst(templatesHere(S.occ)).id, { replay: true });
    arrivedWith = !!((want && OCC_KEYS.includes(want)) || (tq && templateById(tq)) || location.hash.startsWith('#c='));
    if (want || tq) { try { history.replaceState(history.state, '', urlWith({ occ: null, tpl: null })); } catch (e) { /* адрес не критичен */ } }
    renderOccStrip();
    renderTemplates();
    fillTextFields();
    bindText();
    bindStyle();
    bindTextSizes();
    bindLayout();
    bindAnim();
    bindDeco();
    bindHistory();
    bindPhoto();
    bindTransport();
    music.bind();
    exporter.bind();
    bindBatch();

    document.querySelectorAll('.gc-rail [data-tab]').forEach((b) => b.addEventListener('click', () => showTab(b.dataset.tab)));
    // Кнопка — переключатель. Нажатие вне открытого поповера браузер сам
    // закрывает ещё на pointerdown, и click увидел бы меню закрытым и открыл
    // его снова: открытость запоминается до того.
    let menuWasOpen = false;
    $('gcOccAllBtn').addEventListener('pointerdown', () => { menuWasOpen = occMenuOpen(); });
    $('gcOccAllBtn').addEventListener('click', () => {
        const open = menuWasOpen || occMenuOpen();
        menuWasOpen = false;
        if (open) closeOccMenu(); else openOccMenu();
    });
    // Браузер закрыл поповер сам (клик мимо, Esc) — атрибут и кнопка следуют.
    occMenu().addEventListener('toggle', (e) => {
        if (e.newState === 'closed' && occMenuOpen()) closeOccMenu();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && occMenuOpen()) closeOccMenu(); });
    document.addEventListener('pointerdown', (e) => {
        const m = occMenu();
        if (occMenuOpen() && !m.contains(e.target) && !$('gcOccAllBtn').contains(e.target)) closeOccMenu();
    });
    // Законченный шаг ползунка или палитры — правка работы (движение ползунка
    // её только сохраняет, см. docChanged).
    $('gcApp').addEventListener('change', (e) => {
        if (e.target && e.target.matches && e.target.matches('input[type=range], input[type=color]')) touch(true);
    });

    // «Мои открытки» — общий контроллер: он сам решает, открыть ли
    // незаконченную открытку (или ту, что была в этой вкладке до F5).
    if (window.__toolDraft && window.__toolDraft.editor && $('gcDrafts')) {
        drafts = window.__toolDraft.editor({
            slug: SLUG, root: 'gcDrafts', icon: '💌', openKey: 'gc-draft-open',
            payload: draftPayload, apply: applyDraft, fresh: freshCard,
            started: () => arrivedWith,
        });
    }

    // Открытка по ссылке: #c=… — показать её получателю поверх страницы.
    if (location.hash.startsWith('#c=')) {
        const doc = await decodeLink(location.hash.slice(3));
        openViewer({ doc, cfg: C, ui: u, music, onReply: (d) => {
            S.occ = 'thanks';
            setOccasion(OCC_KEYS.includes('thanks') ? 'thanks' : S.occ, 'reply');
        }, onMake: () => { /* редактор уже под окном просмотра */ } });
        funnel('link_open', { ok: !!doc });
    }

    window.__stage && window.__stage('ready');
    funnel('ready', { occ: S.occ, tpl: S.tplId, arrived: arrivedWith ? 1 : 0 });
    histReset();
    reflectTextSizes();
    if (drafts) drafts.boot();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
